import { useRouter } from "next/router";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  useRef,
} from "react";

// eslint-disable-next-line import/no-cycle

// Inject Authorize into DOM
import "@shared/lib/authorize.net/loader";

import { useVenueBasket } from "@app-contexts/dashboard/features/box-office/basket/services/useVenueBasket";
import { DataProvider } from "@shared/constants/common";
import { PAYMENT_GATEWAY_MAP } from "@shared/constants/enums";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import { recordAppError, setTag } from "@shared/utils/errors/errorLogger";
import { PublicInvoiceDetailSerializer } from "@app-contexts/public/features/invoice-staging/data/types/invoice";
import { VenueBasedInvoiceSerializer } from "@app-contexts/dashboard/shared/types/invoice";
import useMountEffect from "@shared/hooks/useMountEffect/useMountEffect";
import { useColorMode } from "@shared/hooks/useColorMode";
import StripePaymentGateway from "../gateways/StripePaymentGateway/StripePaymentGateway";
import {
  EXPRESS_CHECKOUT_QUERY_PARAM,
  EXPRESS_CHECKOUT_INVOICE_ID_PARAM,
  EXPRESS_CHECKOUT_UUID1_PARAM,
  EXPRESS_CHECKOUT_UUID2_PARAM,
} from "../constants/express-checkout";

import { PaymentConfigurationError } from "../utils/errors/PaymentConfigurationError";
import useBasketPaymentService, {
  PaymentServiceBasket,
} from "../services/useBasketPaymentService";
import { shouldUseFreeHandler } from "../utils/handlers/handlers";
import useInvoicePaymentService from "../services/useInvoicePaymentService";
// eslint-disable-next-line import/no-cycle
import PaymentManager, { PaymentManagerConfig } from "../PaymentManager";
import { PurchaseData, TicketBasket } from "../types";

export interface PostPurchaseCallbackProps {
  finalBasket: TicketBasket;
  invoice: VenueBasedInvoiceSerializer | PublicInvoiceDetailSerializer | null;
}

export interface PaymentManagerProviderConfig {
  config: PaymentManagerConfig;
  basket:
    | ReturnType<typeof usePublicBasket>
    | ReturnType<typeof useVenueBasket>;
  basketType: DataProvider;
  children: React.ReactNode;
  onPostPurchase?: (purchaseData: PostPurchaseCallbackProps) => void;
}

const PaymentManagerContext = createContext<{
  manager: PaymentManager;
  isProcessingPurchase: boolean;
  isPurchaseComplete: boolean;
  purchaseData: PurchaseData | null;
  handleSetPurchaseData: (purchaseData: PurchaseData | null) => void;
} | null>(null);

function PaymentManagerProvider({
  config,
  basket,
  basketType,
  children,
  onPostPurchase,
}: PaymentManagerProviderConfig) {
  const basketPaymentService = useBasketPaymentService(basket, basketType);
  const invoicePaymentService = useInvoicePaymentService(basket, basketType);
  const [isProcessingPurchase, setIsProcessingPurchase] = useState(false);
  const [isPurchaseComplete, setIsPurchaseComplete] = useState(false);
  const [purchaseData, setPurchaseData] = useState<PurchaseData | null>(null);

  const hasHandledRedirect = useRef(false);

  const { query, replace, asPath } = useRouter();

  const handleProcessingChange = useCallback((isProcessing: boolean) => {
    setIsProcessingPurchase(isProcessing);
  }, []);

  const handlePurchaseComplete = useCallback((isComplete: boolean) => {
    setIsPurchaseComplete(isComplete);
  }, []);

  const handleSetPurchaseData = useCallback(
    (purchaseData: PurchaseData | null) => {
      setPurchaseData(purchaseData);
    },
    []
  );

  // Single instance of PaymentManager, instantiated when this component mounts
  const [manager] = useState(() => {
    return new PaymentManager({
      config,
      basketPaymentService,
      invoicePaymentService,
      onPostPurchase: (data) => {
        // Set purchaseData for ALL flows (card + wallet). For cards this is
        // also set in Payment.web.tsx after manager.purchase() returns, but
        // for wallet flows (Yuno) the only signal path is through this
        // callback via PaymentManager.finalizePostPurchase().
        setPurchaseData(data);
        onPostPurchase?.(data);
      },
      onPurchaseComplete: handlePurchaseComplete,
      onProcessing: handleProcessingChange,
    });
  });

  const handleBasketUpdate = useCallback(
    (basket: PaymentServiceBasket) => {
      return manager.setBasket(basket);
    },
    [manager.setBasket]
  );

  const initializeManager = useCallback(
    async (basket: PaymentServiceBasket) => {
      try {
        await manager.initialize(basket);
      } catch (err) {
        recordAppError(err);
      }
    },
    [manager.initialize]
  );

  const onSideEffect = useCallback(
    (basket: PaymentServiceBasket) => {
      try {
        // Whenever basket changes, update the object in the manager
        if (
          basket &&
          // Temporarily check for Stripe, Authorize, and Yuno here to prevent error toasts from
          // showing up when a processor not supported by the orchestra (eg. Square) is being used.
          // Once all processors are migrated to use the orchestra, then this can be removed.
          (basket.paymentGateway?.gateway_type ===
            PAYMENT_GATEWAY_MAP.GATEWAY_STRIPE.id ||
            basket.paymentGateway?.gateway_type ===
              PAYMENT_GATEWAY_MAP.GATEWAY_AUTHNET.id ||
            basket.paymentGateway?.gateway_type ===
              PAYMENT_GATEWAY_MAP.GATEWAY_YUNO.id ||
            shouldUseFreeHandler(basket))
        ) {
          handleBasketUpdate(basket);

          // Store whether initialization is needed and why
          const shouldInitialize = shouldInitializeManager(
            basket,
            manager,
            query
          );

          if (shouldInitialize) {
            initializeManager(basket);
          }
        }
      } catch (err) {
        recordAppError(err);
      }
    },
    [manager, query, handleBasketUpdate, initializeManager]
  );

  const cleanQueryParams = useCallback(() => {
    const [pathname, search] = asPath.split("?");
    if (!search) return;

    const params = new URLSearchParams(search);
    // Stripe/Affirm params
    params.delete("payment_intent");
    params.delete("payment_intent_client_secret");
    params.delete("stripe");
    params.delete("affirm");
    // Yuno params
    params.delete("yuno_checkout_session");
    params.delete("yuno_redirect");
    // Express checkout params
    params.delete(EXPRESS_CHECKOUT_QUERY_PARAM);
    params.delete(EXPRESS_CHECKOUT_INVOICE_ID_PARAM);
    params.delete(EXPRESS_CHECKOUT_UUID1_PARAM);
    params.delete(EXPRESS_CHECKOUT_UUID2_PARAM);

    const newSearch = params.toString();
    const newPath = newSearch ? `${pathname}?${newSearch}` : pathname;
    replace(newPath, undefined, { shallow: true });
  }, [replace, asPath]);

  const handleThirdPartyRedirect = useCallback(async () => {
    if (hasHandledRedirect.current) return;
    hasHandledRedirect.current = true;

    try {
      const result = await manager.handleThirdPartyRedirect();
      setPurchaseData(result);
    } catch (err) {
      onSideEffect(basketPaymentService.basket);
    } finally {
      cleanQueryParams();
    }
  }, [
    manager.handleThirdPartyRedirect,
    onSideEffect,
    basketPaymentService.basket,
    cleanQueryParams,
  ]);

  const expressInvoiceId = query[EXPRESS_CHECKOUT_INVOICE_ID_PARAM];
  const expressUuid1 = query[EXPRESS_CHECKOUT_UUID1_PARAM];
  const expressUuid2 = query[EXPRESS_CHECKOUT_UUID2_PARAM];

  const handleExpressCheckoutRedirect = useCallback(async () => {
    if (hasHandledRedirect.current) return;
    hasHandledRedirect.current = true;

    if (typeof expressInvoiceId !== "string" || !expressInvoiceId) {
      cleanQueryParams();
      return;
    }

    const basketUuids =
      typeof expressUuid1 === "string" && typeof expressUuid2 === "string"
        ? { uuid: expressUuid1, uuid2: expressUuid2 }
        : undefined;

    try {
      const invoice = await invoicePaymentService.retrieve(
        expressInvoiceId,
        basketUuids
      );

      let finalBasket;
      const ticketBasket =
        invoice && "ticket_basket" in invoice
          ? invoice.ticket_basket
          : undefined;

      if (ticketBasket?.id && ticketBasket?.uuid) {
        try {
          finalBasket = await basketPaymentService.retrieveById(
            ticketBasket.id,
            ticketBasket.uuid
          );
        } catch (basketErr) {
          recordAppError(basketErr);
        }
      }

      setPurchaseData({ finalBasket, invoice });
      setIsPurchaseComplete(true);
    } catch (err) {
      recordAppError(err);
    } finally {
      cleanQueryParams();
    }
  }, [
    invoicePaymentService.retrieve,
    basketPaymentService.retrieveById,
    cleanQueryParams,
    expressInvoiceId,
    expressUuid1,
    expressUuid2,
  ]);

  /**
   * Routing for checkout page load:
   * 1. Third-party redirect (Stripe/Affirm): payment_intent + payment_intent_client_secret
   * 2. Express checkout redirect: express_checkout=true + invoice_id + uuid1 + uuid2
   * 3. Normal checkout: initialize payment manager
   */
  useEffect(() => {
    // Check for Stripe/Affirm redirect params
    const hasStripeRedirectParams =
      query.payment_intent && query.payment_intent_client_secret;
    // Check for Yuno redirect params
    const hasYunoRedirectParams =
      query.yuno_checkout_session && query.yuno_redirect;

    const hasRedirectParams = hasStripeRedirectParams || hasYunoRedirectParams;

    const isExpressCheckoutRedirect =
      query[EXPRESS_CHECKOUT_QUERY_PARAM] === "true";

    if (
      hasRedirectParams &&
      !hasHandledRedirect.current &&
      basketPaymentService.basket.basketId
    ) {
      handleThirdPartyRedirect();
    } else if (isExpressCheckoutRedirect && !hasHandledRedirect.current) {
      handleExpressCheckoutRedirect();
    } else if (!hasStripeRedirectParams && !isExpressCheckoutRedirect) {
      onSideEffect(basketPaymentService.basket);
    }
  }, [
    onSideEffect,
    basketPaymentService.basket,
    handleThirdPartyRedirect,
    handleExpressCheckoutRedirect,
    query.payment_intent,
    query.payment_intent_client_secret,
    query.yuno_checkout_session,
    query.yuno_redirect,
    query.express_checkout,
  ]);

  // Ensure that the manager object stays up to date by updating the references of the services
  useEffect(() => {
    manager.setServices({
      basketPaymentService,
      invoicePaymentService,
    });
  }, [basketPaymentService, invoicePaymentService]);

  // Keep the shared Stripe elements instance appearance in sync with the
  // active color mode. Individual element styles (card inputs, messaging)
  // are handled by StripeElement itself.
  // Uses colorMode from React state rather than reading the DOM class
  // (isDarkMode), because child effects run before ThemeProvider applies
  // the new class to <html>.
  const { colorMode } = useColorMode();
  useEffect(() => {
    const gateway = manager.handler?.gateway;
    if (gateway instanceof StripePaymentGateway) {
      gateway.updateAppearance(colorMode);
    }
  }, [colorMode, manager.handler?.gateway]);

  useMountEffect(() => {
    setTag("basket_id", manager.basket?.basketId?.toString() ?? "unknown");
    setTag(
      "purchase_source_platform",
      manager.basket?.purchaseSourcePlatform ?? "unknown"
    );
    setTag("venue_id", manager.basket?.paymentVenue?.toString() ?? "unknown");
  });

  useEffect(() => {
    return () => {
      // Reset the cached handler's initialization state so it fully
      // re-initializes (including a fresh elementsInstance) on the next mount.
      // The handler instance itself stays in PaymentHandlerFactory's cache.
      if (manager.handler) {
        manager.handler.is_initialized = false;
        if (manager.handler.gateway) {
          manager.handler.gateway.elementsInstance = undefined;
        }
      }
      manager.initializationPromise = undefined;
    };
  }, []);

  const value = useMemo(
    () => ({
      manager,
      isProcessingPurchase,
      isPurchaseComplete,
      purchaseData,
      handleSetPurchaseData,
    }),
    [
      manager,
      isProcessingPurchase,
      isPurchaseComplete,
      purchaseData,
      handleSetPurchaseData,
    ]
  );

  return (
    <PaymentManagerContext.Provider value={value}>
      {children}
    </PaymentManagerContext.Provider>
  );
}

const getServerSnapshot = (): number => 0;

function usePaymentManager() {
  const context = useContext(PaymentManagerContext);

  if (!context) {
    throw new PaymentConfigurationError(
      "usePaymentManager hook must be used within a PaymentManagerProvider component tree. Please wrap your component with PaymentManagerProvider."
    );
  }

  useSyncExternalStore(
    context.manager.subscribe,
    context.manager.getSnapshot,
    getServerSnapshot
  );

  return context;
}

export { PaymentManagerProvider, usePaymentManager };

function shouldInitializeManager(
  basket: PaymentServiceBasket,
  manager: PaymentManager,
  query: Record<string, unknown>
) {
  // Check for Stripe/Affirm redirect params
  const hasStripeRedirectParams =
    query.payment_intent && query.payment_intent_client_secret;
  // Check for Yuno redirect params
  const hasYunoRedirectParams =
    query.yuno_checkout_session && query.yuno_redirect;

  const isExpressCheckoutRedirect =
    query[EXPRESS_CHECKOUT_QUERY_PARAM] === "true";

  return (
    // requires an active basket to initialize
    basket.basketId &&
    basket.paymentGateway &&
    // doesn't need to initialize again if an initialization is already in progress
    !manager.initializationPromise &&
    // doesn't need to initialize again if a handler is already initialized
    (!manager.handler || !manager.handler.is_initialized) &&
    // doesn't need to initialize if we are handling an inbound redirect
    !hasStripeRedirectParams &&
    !hasYunoRedirectParams &&
    !isExpressCheckoutRedirect
  );
}
